import React, { useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Chip, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealBase, IFolder } from "store/types";

import { Folder } from "./Folder";

const DIVIDER = "1px solid #E5E8EF";

interface IFolderSelectProps {
  deal: DealBase | null;
  folders: IFolder[];
  loading: boolean;
  selectedFolder: IFolder | null;
  setSelectedFolder: (folder: IFolder | null) => void;
  onCreateNewFolder: () => void;
  onStepBack: () => void;
  hasNewFolder: boolean;
  isGeneralDocument?: boolean;
}

export const FolderSelect: React.FC<IFolderSelectProps> = ({
  deal,
  folders,
  loading,
  selectedFolder,
  setSelectedFolder,
  onCreateNewFolder,
  onStepBack,
  hasNewFolder,
  isGeneralDocument
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 30 }}>
      {isGeneralDocument && (
        <Stack
          direction="row"
          alignItems="center"
          gap="6px"
          sx={{
            px: "20px",
            pb: "10px",
            cursor: "pointer",
            width: "fit-content",
            maxWidth: "calc(100% - 40px)",
            "&:hover": { opacity: 0.8 }
          }}
          onClick={onStepBack}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: 20, color: "#071F35" }} />
          <MaterialTooltip title={deal?.title} placement="top">
            <Typography noWrap sx={{ fontSize: 14, color: "#071F35", fontWeight: 600 }}>
              {deal?.title}
            </Typography>
          </MaterialTooltip>
        </Stack>
      )}

      {/* Search */}
      <Stack direction="row" alignItems="center" gap="12px" sx={{ px: "20px", py: "12px" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search folder..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ "& .MuiInputBase-root": { height: 32 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            )
          }}
        />
      </Stack>

      {/* Content area */}
      <Box sx={{ minHeight: 360, overflowY: "auto", flex: 1, background: "#fff" }}>
        <Box sx={{ border: DIVIDER, borderRadius: "8px", mx: "20px", my: "16px" }}>
          {folders.map((folder) => (
            <Folder
              key={folder.id}
              folder={folder}
              subIndex={0}
              setSelectedFolder={setSelectedFolder}
              selectedFolder={selectedFolder}
              searchQuery={searchQuery}
            />
          ))}
          {folders.length === 0 && (
            <Box sx={{ py: "56px", px: "20px", textAlign: "center" }}>
              <FolderOutlinedIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No folders in workspace</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Add Folder */}
      {!hasNewFolder && (
        <Box sx={{ px: "20px", py: "10px" }}>
          <Chip
            icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
            label="Add Folder"
            onClick={onCreateNewFolder}
            size="small"
            sx={{ bgcolor: "#f6f8fc" }}
          />
        </Box>
      )}
    </AsyncContentMui>
  );
};
